import type { FormEvent } from 'react';
import { useState } from 'react';

import {
  Alert,
  FormGroup,
  FormLabel,
  Input,
  LoadingButton,
} from '@admin-platform/ui/components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '../../components/Button/Button';

import styles from './ResetPassword.module.css';
import { AuthFooter } from '../../components/AuthCardChrome/AuthCardChrome';
import RevvlyMark from '../../components/Layout/revvly-mark.svg?react';

// `??` so an intentionally-empty VITE_API_ENDPOINT (staging/preview, worker
// proxies API same-origin) stays "" → relative URLs, not localhost.
const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT ?? 'http://localhost:4200';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [tokenStatus, setTokenStatus] = useState<'validating' | 'valid' | 'invalid'>(
    token ? 'valid' : 'invalid',
  );
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });

      if (response.ok) {
        setSuccessMessage('Your password has been updated.');
      } else {
        let detail = 'Something went wrong. Please try again.';
        try {
          const body = await response.json();
          if (body.message) detail = body.message;
        } catch {
          // ignore parse errors
        }

        const isTokenError =
          detail.toLowerCase().includes('invalid') ||
          detail.toLowerCase().includes('expired');

        if ((response.status === 400 || response.status === 404) && isTokenError) {
          setTokenStatus('invalid');
        } else {
          setErrorMessage(detail);
        }
      }
    } catch {
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <RevvlyMark className={styles.mark} aria-hidden="true" />
        <h1 className={styles.logo}>Revvly</h1>
        <p className={styles.subtitle}>Set a new password</p>

        {tokenStatus === 'invalid' && (
          <Alert type="error" className={styles.alertBlock}>
            <div>
              This reset link is invalid or has expired.
              <Link to="/forgot-password" className={styles.errorStateLink}>
                Request a new reset link
              </Link>
            </div>
          </Alert>
        )}

        {tokenStatus === 'validating' && (
          <p className={styles.verifying}>Verifying link...</p>
        )}

        {tokenStatus === 'valid' && successMessage && (
          <>
            <Alert type="success" className={styles.alertBlock}>
              {successMessage}
            </Alert>
            <div className={styles.actions}>
              <Button variant="primary" onClick={() => navigate('/login')}>
                Back to login
              </Button>
            </div>
          </>
        )}

        {tokenStatus === 'valid' && !successMessage && (
          <>
            {errorMessage && (
              <Alert type="error" className={styles.alertBlock}>
                {errorMessage}
              </Alert>
            )}

            <form className={styles.form} onSubmit={handleSubmit}>
              <FormGroup>
                <FormLabel htmlFor="reset-new-password">New password</FormLabel>
                <Input
                  id="reset-new-password"
                  className={styles.input}
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
                <span
                  className={
                    newPassword.length === 0
                      ? styles.hint
                      : newPassword.length < 8
                        ? styles.hintError
                        : styles.hintSuccess
                  }
                >
                  Minimum 8 characters
                </span>
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="reset-confirm-password">
                  Confirm new password
                </FormLabel>
                <Input
                  id="reset-confirm-password"
                  className={styles.input}
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </FormGroup>

              <LoadingButton
                type="submit"
                block
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                Set new password
              </LoadingButton>
            </form>

            <Link className={styles.backLink} to="/login">
              Back to login
            </Link>
          </>
        )}
        <AuthFooter />
      </div>
    </div>
  );
}
